// Seagull Flocking System
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GPUComputationRenderer } from 'three/addons/misc/GPUComputationRenderer.js';

// Production logging utility
const log = {
    debug: (message, ...args) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(message, ...args);
        }
    },
    error: (message, ...args) => console.error(message, ...args),
    warn: (message, ...args) => console.warn(message, ...args)
};

export class SeagullFlock {
    constructor(scene, renderer) {
        if (!scene || !renderer) {
            throw new Error('SeagullFlock requires scene and renderer parameters');
        }
        
        this.scene = scene;
        this.renderer = renderer;
        this.seagulls = [];
        this.birdGeometry = null;
        this.birdMesh = null;
        this.materialShader = null;
        this.textureAnimation = null;
        this.durationAnimation = 0;
        this.indicesPerBird = 0;
        this.isInitialized = false;
        this.hasError = false;
        
        // Flocking parameters - smaller bounds for closer island flying
        this.WIDTH = 32; // Reduced for 5 birds
        this.BIRDS = this.WIDTH * this.WIDTH;
        this.BOUNDS = 50; // Reduced bounds to keep seagulls closer to island
        this.BOUNDS_HALF = this.BOUNDS / 2;
        
        // GPU computation
        this.gpuCompute = null;
        this.velocityVariable = null;
        this.positionVariable = null;
        this.positionUniforms = null;
        this.velocityUniforms = null;
        
        // Animation
        this.last = performance.now();
        this.mouseX = 0;
        this.mouseY = 0;
        
        this.init();
    }
    
    init() {
        this.loadSeagullModel();
    }
    
    loadSeagullModel() {
        log.debug('🦅 Loading seagull model (GLB)...');
        const loader = new GLTFLoader();
        
        // Add DRACO loader for compressed GLB files
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
        loader.setDRACOLoader(dracoLoader);
        
        loader.load('./models/seagull/seagull.glb', (gltf) => {
            log.debug('🦅 Seagull GLB model loaded successfully');
            this.setupSeagullGeometry(gltf);
        }, (progress) => {
            log.debug('🦅 Loading progress:', (progress.loaded / progress.total * 100).toFixed(1) + '%');
        }, (error) => {
            log.error('❌ Error loading seagull GLB model:', error);
            log.debug('🦅 Attempting to load fallback GLTF file...');
            this.loadFallbackGLTF();
        });
    }
    
    loadFallbackGLTF() {
        log.debug('🦅 Loading fallback GLTF model...');
        const loader = new GLTFLoader();
        loader.load('./models/seagull/scene.gltf', (gltf) => {
            log.debug('🦅 Fallback GLTF model loaded successfully');
            this.setupSeagullGeometry(gltf);
        }, (progress) => {
            log.debug('🦅 Fallback loading progress:', (progress.loaded / progress.total * 100).toFixed(1) + '%');
        }, (error) => {
            log.error('❌ Error loading fallback GLTF model:', error);
            log.debug('🦅 Creating basic fallback seagull geometry...');
            this.createFallbackSeagull();
        });
    }
    
    createFallbackSeagull() {
        log.debug('🦅 Creating fallback seagull geometry...');
        
        // Create a more detailed seagull shape using basic geometry
        const seagullGeometry = new THREE.BufferGeometry();
        
        // Create a more realistic seagull shape
        const vertices = [
            // Body (elongated triangle)
            0, 0, 0,      // beak tip
            0.8, 0, 0.1,  // body back
            0.8, 0, -0.1, // body back
            
            // Wings (larger triangles)
            -0.1, 0, 0,   // wing base
            0.4, 0, 0.8,  // wing tip right
            0.4, 0, -0.8, // wing tip left
            
            // Tail
            0.9, 0, 0.2,  // tail right
            0.9, 0, -0.2, // tail left
            1.0, 0, 0,    // tail tip
        ];
        
        const indices = [
            0, 1, 2,  // body
            3, 4, 5,  // wings
            1, 6, 8,  // tail right
            2, 7, 8,  // tail left
        ];
        
        const colors = [
            1, 1, 1,  // white body
            1, 1, 1,
            1, 1, 1,
            0.9, 0.9, 0.9,  // slightly off-white wings
            0.9, 0.9, 0.9,
            0.9, 0.9, 0.9,
            0.95, 0.95, 0.95, // tail
            0.95, 0.95, 0.95,
            1, 1, 1,
        ];
        
        seagullGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
        seagullGeometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
        seagullGeometry.setIndex(indices);
        
        // Create a mock GLTF object
        const mockGltf = {
            scene: {
                children: [{
                    geometry: seagullGeometry
                }]
            },
            animations: []
        };
        
        this.setupSeagullGeometry(mockGltf);
    }
    
    setupSeagullGeometry(gltf) {
        try {
            log.debug('🦅 Setting up seagull geometry from GLB...');
            
            // Check if we have a valid scene with children
            if (!gltf.scene || !gltf.scene.children || gltf.scene.children.length === 0) {
                log.error('❌ Invalid seagull model structure');
                log.debug('🦅 GLTF scene structure:', gltf.scene);
                this.hasError = true;
                return;
            }
        
        log.debug('🦅 GLTF scene children count:', gltf.scene.children.length);
        log.debug('🦅 GLTF animations count:', gltf.animations ? gltf.animations.length : 0);
        
        const animations = gltf.animations;
        if (animations && animations.length > 0) {
            this.durationAnimation = Math.round(animations[0].duration * 60);
            log.debug('🦅 Found animation, duration:', this.durationAnimation);
            log.debug('🦅 Animation name:', animations[0].name);
        } else {
            this.durationAnimation = 60; // Default animation duration
            log.debug('🦅 No animation found, using default duration');
        }
        
        // Find geometry by traversing the scene hierarchy
        let birdGeo = null;
        let foundMesh = null;
        
        log.debug('🦅 Traversing scene to find geometry...');
        gltf.scene.traverse((child) => {
            log.debug('🦅 Checking child:', child.name, 'type:', child.type, 'isMesh:', child.isMesh);
            if (child.isMesh && child.geometry) {
                log.debug('🦅 Found mesh:', child.name, 'with geometry');
                log.debug('🦅 Geometry attributes:', Object.keys(child.geometry.attributes));
                birdGeo = child.geometry;
                foundMesh = child;
                return;
            }
        });
        
        if (!birdGeo) {
            console.error('❌ No geometry found in seagull model');
            log.debug('🦅 Scene structure:');
            this.logSceneStructure(gltf.scene);
            
            // Try to find any geometry in the scene
            log.debug('🦅 Attempting to find any geometry in scene...');
            let anyGeometry = null;
            gltf.scene.traverse((child) => {
                if (child.geometry && !anyGeometry) {
                    log.debug('🦅 Found alternative geometry in:', child.name);
                    anyGeometry = child.geometry;
                }
            });
            
            if (anyGeometry) {
                log.debug('🦅 Using alternative geometry');
                birdGeo = anyGeometry;
            } else {
                log.error('❌ No geometry found anywhere in the model');
                log.debug('🦅 Creating geometry from scene bounds...');
                this.hasError = true; // Mark as error state
                birdGeo = this.createGeometryFromScene(gltf.scene);
                if (!birdGeo) {
                    return;
                }
            }
        }
        
        log.debug('🦅 Using geometry from mesh:', foundMesh.name);
        
        // Safely check for morph attributes
        const morphAttributes = birdGeo.morphAttributes ? birdGeo.morphAttributes.position : null;
        
        log.debug('🦅 Bird geometry vertices:', birdGeo.getAttribute('position').count);
        log.debug('🦅 Bird geometry indices:', birdGeo.index ? birdGeo.index.count : 'none');
        log.debug('🦅 Morph attributes:', morphAttributes ? morphAttributes.length : 0);
        log.debug('🦅 Geometry attributes:', Object.keys(birdGeo.attributes));
        
        // Create animation texture
        this.createAnimationTexture(birdGeo, morphAttributes);
        
        // Create bird geometry
        this.createBirdGeometry(birdGeo);
        
        // Initialize GPU computation
        log.debug('🦅 Initializing GPU computation...');
        const gpuSuccess = this.initComputeRenderer();
        if (!gpuSuccess) {
            log.error('❌ Failed to initialize GPU computation, skipping seagull flock');
            this.hasError = true;
            return;
        }
        
        // Initialize birds
        log.debug('🦅 Initializing birds...');
        this.initBirds();
        
        this.isInitialized = true;
        log.debug('🦅 Seagull flock setup complete! isInitialized =', this.isInitialized);
        } catch (error) {
            log.error('❌ Error in setupSeagullGeometry:', error);
            this.hasError = true;
        }
    }
    
    logSceneStructure(object, depth = 0) {
        const indent = '  '.repeat(depth);
        log.debug(`${indent}${object.name || 'unnamed'} (${object.type})`);
        
        if (object.children) {
            object.children.forEach(child => {
                this.logSceneStructure(child, depth + 1);
            });
        }
    }
    
    createGeometryFromScene(scene) {
        log.debug('🦅 Creating geometry from scene bounds...');
        
        // Calculate scene bounds
        const box = new THREE.Box3().setFromObject(scene);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        
        log.debug('🦅 Scene bounds:', box);
        log.debug('🦅 Scene size:', size);
        log.debug('🦅 Scene center:', center);
        
        if (size.length() === 0) {
            console.error('❌ Scene has no bounds');
            return null;
        }
        
        // Create a simple seagull geometry based on scene bounds
        const geometry = new THREE.BufferGeometry();
        
        // Create a simple bird shape
        const vertices = [
            // Body
            0, 0, 0,      // beak
            size.x * 0.8, 0, size.z * 0.1,  // body back
            size.x * 0.8, 0, -size.z * 0.1, // body back
            
            // Wings
            size.x * 0.2, 0, 0,   // wing base
            size.x * 0.6, 0, size.z * 0.8,  // wing tip right
            size.x * 0.6, 0, -size.z * 0.8, // wing tip left
        ];
        
        const indices = [
            0, 1, 2,  // body
            3, 4, 5,  // wings
        ];
        
        const colors = [
            1, 1, 1,  // white
            1, 1, 1,
            1, 1, 1,
            0.9, 0.9, 0.9,  // slightly off-white wings
            0.9, 0.9, 0.9,
            0.9, 0.9, 0.9,
        ];
        
        geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
        geometry.setIndex(indices);
        
        log.debug('🦅 Created geometry from scene bounds');
        return geometry;
    }
    
    createAnimationTexture(birdGeo, morphAttributes) {
        const nextPowerOf2 = (n) => {
            return Math.pow(2, Math.ceil(Math.log(n) / Math.log(2)));
        };
        
        const tHeight = nextPowerOf2(this.durationAnimation);
        const tWidth = nextPowerOf2(birdGeo.getAttribute('position').count);
        this.indicesPerBird = birdGeo.index ? birdGeo.index.count : 0;
        const tData = new Float32Array(4 * tWidth * tHeight);
        
        log.debug('🦅 Creating animation texture:', tWidth, 'x', tHeight);
        log.debug('🦅 Indices per bird:', this.indicesPerBird);
        
        // Fill animation texture with morph target data
        for (let i = 0; i < tWidth; i++) {
            for (let j = 0; j < tHeight; j++) {
                const offset = j * tWidth * 4;
                
                if (morphAttributes && morphAttributes.length > 0) {
                    const curMorph = Math.floor(j / this.durationAnimation * morphAttributes.length);
                    const nextMorph = (Math.floor(j / this.durationAnimation * morphAttributes.length) + 1) % morphAttributes.length;
                    const lerpAmount = j / this.durationAnimation * morphAttributes.length % 1;
                    
                    if (j < this.durationAnimation) {
                        let d0, d1;
                        
                        d0 = morphAttributes[curMorph].array[i * 3];
                        d1 = morphAttributes[nextMorph].array[i * 3];
                        if (d0 !== undefined && d1 !== undefined) {
                            tData[offset + i * 4] = this.lerp(d0, d1, lerpAmount);
                        }
                        
                        d0 = morphAttributes[curMorph].array[i * 3 + 1];
                        d1 = morphAttributes[nextMorph].array[i * 3 + 1];
                        if (d0 !== undefined && d1 !== undefined) {
                            tData[offset + i * 4 + 1] = this.lerp(d0, d1, lerpAmount);
                        }
                        
                        d0 = morphAttributes[curMorph].array[i * 3 + 2];
                        d1 = morphAttributes[nextMorph].array[i * 3 + 2];
                        if (d0 !== undefined && d1 !== undefined) {
                            tData[offset + i * 4 + 2] = this.lerp(d0, d1, lerpAmount);
                        }
                        
                        tData[offset + i * 4 + 3] = 1;
                    }
                } else {
                    // No morph targets, use base geometry with enhanced animation
                    const position = birdGeo.getAttribute('position');
                    if (position && position.array[i * 3] !== undefined) {
                        // Enhanced wing flapping animation for GLB models
                        const time = j * 0.1;
                        const wingFlap = Math.sin(time) * 0.15;
                        const bodyBob = Math.sin(time * 2) * 0.05;
                        
                        tData[offset + i * 4] = position.array[i * 3];
                        tData[offset + i * 4 + 1] = position.array[i * 3 + 1] + wingFlap + bodyBob;
                        tData[offset + i * 4 + 2] = position.array[i * 3 + 2];
                        tData[offset + i * 4 + 3] = 1;
                    }
                }
            }
        }
        
        this.textureAnimation = new THREE.DataTexture(tData, tWidth, tHeight, THREE.RGBAFormat, THREE.FloatType);
        this.textureAnimation.needsUpdate = true;
        log.debug('🦅 Animation texture created successfully for GLB model');
    }
    
    createBirdGeometry(birdGeo) {
        log.debug('🦅 Creating bird geometry...');
        
        this.birdGeometry = new THREE.BufferGeometry();
        
        const vertices = [], color = [], reference = [], seeds = [], indices = [];
        const totalVertices = birdGeo.getAttribute('position').count * 3 * this.BIRDS;
        
        // Create vertices
        for (let i = 0; i < totalVertices; i++) {
            const bIndex = i % (birdGeo.getAttribute('position').count * 3);
            vertices.push(birdGeo.getAttribute('position').array[bIndex]);
            
            // Use white color for seagulls
            color.push(1, 1, 1);
        }
        
        // Create reference and seeds
        let r = Math.random();
        for (let i = 0; i < birdGeo.getAttribute('position').count * this.BIRDS; i++) {
            const bIndex = i % (birdGeo.getAttribute('position').count);
            const bird = Math.floor(i / birdGeo.getAttribute('position').count);
            if (bIndex == 0) r = Math.random();
            const j = ~~bird;
            const x = (j % this.WIDTH) / this.WIDTH;
            const y = ~~(j / this.WIDTH) / this.WIDTH;
            reference.push(x, y, bIndex / this.textureAnimation.image.width, this.durationAnimation / this.textureAnimation.image.height);
            seeds.push(bird, r, Math.random(), Math.random());
        }
        
        // Create indices (handle case where index might not exist)
        if (birdGeo.index && birdGeo.index.array) {
            for (let i = 0; i < birdGeo.index.array.length * this.BIRDS; i++) {
                const offset = Math.floor(i / birdGeo.index.array.length) * (birdGeo.getAttribute('position').count);
                indices.push(birdGeo.index.array[i % birdGeo.index.array.length] + offset);
            }
        } else {
            // Create sequential indices if no index exists
            for (let i = 0; i < birdGeo.getAttribute('position').count * this.BIRDS; i++) {
                indices.push(i);
            }
        }
        
        this.birdGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
        this.birdGeometry.setAttribute('birdColor', new THREE.BufferAttribute(new Float32Array(color), 3));
        this.birdGeometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(color), 3));
        this.birdGeometry.setAttribute('reference', new THREE.BufferAttribute(new Float32Array(reference), 4));
        this.birdGeometry.setAttribute('seeds', new THREE.BufferAttribute(new Float32Array(seeds), 4));
        this.birdGeometry.setIndex(indices);
        
        log.debug('🦅 Bird geometry created with', vertices.length / 3, 'vertices');
    }
    
    initComputeRenderer() {
        try {
            log.debug('🦅 Initializing GPU compute renderer...');
            this.gpuCompute = new GPUComputationRenderer(this.WIDTH, this.WIDTH, this.renderer);
            
            const dtPosition = this.gpuCompute.createTexture();
            const dtVelocity = this.gpuCompute.createTexture();
            this.fillPositionTexture(dtPosition);
            this.fillVelocityTexture(dtVelocity);
            
            // Create shader materials for position and velocity
            this.velocityVariable = this.gpuCompute.addVariable('textureVelocity', this.getVelocityShader(), dtVelocity);
            this.positionVariable = this.gpuCompute.addVariable('texturePosition', this.getPositionShader(), dtPosition);
            
            this.gpuCompute.setVariableDependencies(this.velocityVariable, [this.positionVariable, this.velocityVariable]);
            this.gpuCompute.setVariableDependencies(this.positionVariable, [this.positionVariable, this.velocityVariable]);
            
            this.positionUniforms = this.positionVariable.material.uniforms;
            this.velocityUniforms = this.velocityVariable.material.uniforms;
            
                    // Set uniforms - adjusted for island-orbiting behavior
        this.positionUniforms['time'] = { value: 0.0 };
        this.positionUniforms['delta'] = { value: 0.0 };
        this.velocityUniforms['time'] = { value: 1.0 };
        this.velocityUniforms['delta'] = { value: 0.0 };
        this.velocityUniforms['testing'] = { value: 1.0 };
        this.velocityUniforms['separationDistance'] = { value: 12.0 }; // Closer separation
        this.velocityUniforms['alignmentDistance'] = { value: 25.0 }; // Closer alignment
        this.velocityUniforms['cohesionDistance'] = { value: 12.0 }; // Closer cohesion
        this.velocityUniforms['freedomFactor'] = { value: 0.6 }; // Less random movement
        this.velocityUniforms['predator'] = { value: new THREE.Vector3() };
        this.velocityVariable.material.defines.BOUNDS = this.BOUNDS.toFixed(2);
            
            this.velocityVariable.wrapS = THREE.RepeatWrapping;
            this.velocityVariable.wrapT = THREE.RepeatWrapping;
            this.positionVariable.wrapS = THREE.RepeatWrapping;
            this.positionVariable.wrapT = THREE.RepeatWrapping;
            
            const error = this.gpuCompute.init();
            if (error !== null) {
                console.error('❌ GPU Compute error:', error);
                return false;
            }
            
            log.debug('🦅 GPU compute renderer initialized successfully');
            return true;
        } catch (error) {
            console.error('❌ Error initializing GPU compute renderer:', error);
            return false;
        }
    }
    
    initBirds() {
        const geometry = this.birdGeometry;
        
        const material = new THREE.MeshStandardMaterial({
            vertexColors: true,
            flatShading: true,
            roughness: 1,
            metalness: 0
        });
        
        material.onBeforeCompile = (shader) => {
            shader.uniforms.texturePosition = { value: null };
            shader.uniforms.textureVelocity = { value: null };
            shader.uniforms.textureAnimation = { value: this.textureAnimation };
            shader.uniforms.time = { value: 1.0 };
            shader.uniforms.size = { value: 0.4 }; // Slightly smaller for closer island flying
            shader.uniforms.delta = { value: 0.0 };
            
            let token = '#define STANDARD';
            let insert = /* glsl */`
                attribute vec4 reference;
                attribute vec4 seeds;
                attribute vec3 birdColor;
                uniform sampler2D texturePosition;
                uniform sampler2D textureVelocity;
                uniform sampler2D textureAnimation;
                uniform float size;
                uniform float time;
            `;
            
            shader.vertexShader = shader.vertexShader.replace(token, token + insert);
            
            token = '#include <begin_vertex>';
            insert = /* glsl */`
                vec4 tmpPos = texture2D(texturePosition, reference.xy);
                
                vec3 pos = tmpPos.xyz;
                vec3 velocity = normalize(texture2D(textureVelocity, reference.xy).xyz);
                vec3 aniPos = texture2D(textureAnimation, vec2(reference.z, mod(time + (seeds.x) * ((0.0004 + seeds.y / 10000.0) + normalize(velocity) / 20000.0), reference.w))).xyz;
                vec3 newPosition = position;
                
                newPosition = mat3(modelMatrix) * (newPosition + aniPos);
                newPosition *= size + seeds.y * size * 0.2;
                
                velocity.z *= -1.;
                float xz = length(velocity.xz);
                float xyz = 1.;
                float x = sqrt(1. - velocity.y * velocity.y);
                
                float cosry = velocity.x / xz;
                float sinry = velocity.z / xz;
                
                float cosrz = x / xyz;
                float sinrz = velocity.y / xyz;
                
                mat3 maty = mat3(cosry, 0, -sinry, 0, 1, 0, sinry, 0, cosry);
                mat3 matz = mat3(cosrz, sinrz, 0, -sinrz, cosrz, 0, 0, 0, 1);
                
                newPosition = maty * matz * newPosition;
                newPosition += pos;
                
                vec3 transformed = vec3(newPosition);
            `;
            
            shader.vertexShader = shader.vertexShader.replace(token, insert);
            this.materialShader = shader;
        };
        
        this.birdMesh = new THREE.Mesh(geometry, material);
        this.birdMesh.rotation.y = Math.PI / 2;
        this.birdMesh.castShadow = true;
        this.birdMesh.receiveShadow = true;
        
        // Position seagulls at appropriate height above the island
        this.birdMesh.position.y = 30; // Higher position to ensure above water
        this.birdMesh.position.x = 0;
        this.birdMesh.position.z = 0;
        
        this.scene.add(this.birdMesh);
        
        // Set draw range for 5 birds (handle case where indicesPerBird might be 0)
        if (this.indicesPerBird > 0) {
            this.birdMesh.geometry.setDrawRange(0, this.indicesPerBird * 5);
        } else {
            // Fallback: use a reasonable number of vertices per bird
            const verticesPerBird = this.birdGeometry.getAttribute('position').count / this.BIRDS;
            this.birdMesh.geometry.setDrawRange(0, verticesPerBird * 5);
        }
        
        log.debug('🦅 Birds initialized with draw range for 5 seagulls');
    }
    
    fillPositionTexture(texture) {
        const theArray = texture.image.data;
        
        for (let k = 0, kl = theArray.length; k < kl; k += 4) {
            // Create circular starting positions around the island
            const angle = (k / kl) * Math.PI * 2;
            const radius = 15 + Math.random() * 8; // 15-23 units from center (closer)
            const height = 25 + Math.random() * 5; // 25-30 units high (above water)
            
            const x = Math.cos(angle) * radius;
            const y = height;
            const z = Math.sin(angle) * radius;
            
            theArray[k + 0] = x;
            theArray[k + 1] = y;
            theArray[k + 2] = z;
            theArray[k + 3] = 1;
        }
    }
    
    fillVelocityTexture(texture) {
        const theArray = texture.image.data;
        
        for (let k = 0, kl = theArray.length; k < kl; k += 4) {
            // Create circular initial velocities for island orbiting
            const angle = (k / kl) * Math.PI * 2;
            const speed = 2 + Math.random() * 1; // 2-3 units per second (slower)
            
            // Tangential velocity for circular motion
            const x = -Math.sin(angle) * speed;
            const y = (Math.random() - 0.5) * 0.5; // Very small vertical variation
            const z = Math.cos(angle) * speed;
            
            theArray[k + 0] = x;
            theArray[k + 1] = y;
            theArray[k + 2] = z;
            theArray[k + 3] = 1;
        }
    }
    
    update() {
        // Debug seagull state
        if (this.hasError) {
            log.debug('Seagull update skipped: hasError = true');
            return;
        }
        
        if (!this.isInitialized) {
            log.debug('Seagull update skipped: not initialized');
            return;
        }
        
        if (!this.gpuCompute || !this.materialShader) {
            log.debug('Seagull update skipped: missing GPU compute or shader');
            return;
        }
        
        try {
            const now = performance.now();
            let delta = (now - this.last) / 1000;
            
            if (delta > 1) delta = 1; // safety cap on large deltas
            this.last = now;
            
            // Validate uniforms before using them
            if (!this.positionUniforms || !this.velocityUniforms) {
                log.warn('Uniforms not available, skipping seagull update');
                return;
            }
            
            this.positionUniforms['time'].value = now;
            this.positionUniforms['delta'].value = delta;
            this.velocityUniforms['time'].value = now;
            this.velocityUniforms['delta'].value = delta;
            
            if (this.materialShader && this.materialShader.uniforms) {
                this.materialShader.uniforms['time'].value = now / 1000;
                this.materialShader.uniforms['delta'].value = delta;
            }
            
            // Set predator position (can be used for interaction)
            this.velocityUniforms['predator'].value.set(0, 0, 0);
            
            this.gpuCompute.compute();
            
            if (this.materialShader) {
                this.materialShader.uniforms['texturePosition'].value = this.gpuCompute.getCurrentRenderTarget(this.positionVariable).texture;
                this.materialShader.uniforms['textureVelocity'].value = this.gpuCompute.getCurrentRenderTarget(this.velocityVariable).texture;
            }
        } catch (error) {
            console.error('❌ Error updating seagull flock:', error);
        }
    }
    
    getPositionShader() {
        return `
            uniform float time;
            uniform float delta;
            
            void main() {
                vec2 uv = gl_FragCoord.xy / resolution.xy;
                vec4 tmpPos = texture2D(texturePosition, uv);
                vec3 position = tmpPos.xyz;
                vec3 velocity = texture2D(textureVelocity, uv).xyz;
                
                float phase = tmpPos.w;
                
                phase = mod((phase + delta + length(velocity.xz) * delta * 3. + max(velocity.y, 0.0) * delta * 6.), 62.83);
                
                gl_FragColor = vec4(position + velocity * delta * 15., phase);
            }
        `;
    }
    
    getVelocityShader() {
        return `
            uniform float time;
            uniform float testing;
            uniform float delta;
            uniform float separationDistance;
            uniform float alignmentDistance;
            uniform float cohesionDistance;
            uniform float freedomFactor;
            uniform vec3 predator;
            
            const float width = resolution.x;
            const float height = resolution.y;
            
            const float PI = 3.141592653589793;
            const float PI_2 = PI * 2.0;
            
            float zoneRadius = 40.0;
            float zoneRadiusSquared = 1600.0;
            
            float separationThresh = 0.45;
            float alignmentThresh = 0.65;
            
            const float UPPER_BOUNDS = BOUNDS;
            const float LOWER_BOUNDS = -UPPER_BOUNDS;
            
            const float SPEED_LIMIT = 4.0; // Reduced speed limit for slower flight
            
            float rand(vec2 co) {
                return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
            }
            
            void main() {
                zoneRadius = separationDistance + alignmentDistance + cohesionDistance;
                separationThresh = separationDistance / zoneRadius;
                alignmentThresh = (separationDistance + alignmentDistance) / zoneRadius;
                zoneRadiusSquared = zoneRadius * zoneRadius;
                
                vec2 uv = gl_FragCoord.xy / resolution.xy;
                vec3 birdPosition, birdVelocity;
                
                vec3 selfPosition = texture2D(texturePosition, uv).xyz;
                vec3 selfVelocity = texture2D(textureVelocity, uv).xyz;
                
                float dist;
                vec3 dir;
                float distSquared;
                
                float separationSquared = separationDistance * separationDistance;
                float cohesionSquared = cohesionDistance * cohesionDistance;
                
                float f;
                float percent;
                
                vec3 velocity = selfVelocity;
                
                float limit = SPEED_LIMIT;
                
                // Predator avoidance
                dir = predator * UPPER_BOUNDS - selfPosition;
                dir.z = 0.;
                dist = length(dir);
                distSquared = dist * dist;
                
                float preyRadius = 150.0;
                float preyRadiusSq = preyRadius * preyRadius;
                
                if (dist < preyRadius) {
                    f = (distSquared / preyRadiusSq - 1.0) * delta * 100.;
                    velocity += normalize(dir) * f;
                    limit += 5.0;
                }
                
                // Island-orbiting attraction - create circular pattern around island
                vec3 central = vec3(0., 0., 0.);
                dir = selfPosition - central;
                dist = length(dir);
                
                // Keep seagulls at a specific distance from island center
                float targetDistance = 20.0; // Closer to island
                if (dist > targetDistance + 8.0) {
                    // Too far from island, move closer
                    velocity -= normalize(dir) * delta * 6.0; // Stronger attraction
                } else if (dist < targetDistance - 8.0) {
                    // Too close to island, move away slightly
                    velocity += normalize(dir) * delta * 3.0; // Moderate repulsion
                }
                
                // Add circular motion around the island (slower)
                vec3 tangent = vec3(-dir.z, 0.0, dir.x);
                velocity += normalize(tangent) * delta * 1.5; // Reduced speed
                
                // Keep seagulls above water level (minimum 20 units above ground)
                if (selfPosition.y > 40.0) {
                    velocity.y -= delta * 15.0; // Strong downward force if too high
                } else if (selfPosition.y < 20.0) {
                    velocity.y += delta * 20.0; // Strong upward force if too low (near water)
                }
                
                // Flocking behavior
                for (float y = 0.0; y < height; y++) {
                    for (float x = 0.0; x < width; x++) {
                        vec2 ref = vec2(x + 0.5, y + 0.5) / resolution.xy;
                        birdPosition = texture2D(texturePosition, ref).xyz;
                        
                        dir = birdPosition - selfPosition;
                        dist = length(dir);
                        
                        if (dist < 0.0001) continue;
                        
                        distSquared = dist * dist;
                        
                        if (distSquared > zoneRadiusSquared) continue;
                        
                        percent = distSquared / zoneRadiusSquared;
                        
                        if (percent < separationThresh) {
                            // Separation
                            f = (separationThresh / percent - 1.0) * delta;
                            velocity -= normalize(dir) * f;
                        } else if (percent < alignmentThresh) {
                            // Alignment
                            float threshDelta = alignmentThresh - separationThresh;
                            float adjustedPercent = (percent - separationThresh) / threshDelta;
                            
                            birdVelocity = texture2D(textureVelocity, ref).xyz;
                            
                            f = (0.5 - cos(adjustedPercent * PI_2) * 0.5 + 0.5) * delta;
                            velocity += normalize(birdVelocity) * f;
                        } else {
                            // Cohesion
                            float threshDelta = 1.0 - alignmentThresh;
                            float adjustedPercent;
                            if (threshDelta == 0.) adjustedPercent = 1.;
                            else adjustedPercent = (percent - alignmentThresh) / threshDelta;
                            
                            f = (0.5 - (cos(adjustedPercent * PI_2) * -0.5 + 0.5)) * delta;
                            velocity += normalize(dir) * f;
                        }
                    }
                }
                
                // Speed limit
                if (length(velocity) > limit) {
                    velocity = normalize(velocity) * limit;
                }
                
                gl_FragColor = vec4(velocity, 1.0);
            }
        `;
    }
    
    lerp(start, end, amount) {
        amount = Math.max(Math.min(amount, 1), 0);
        return start + (end - start) * amount;
    }
}
